import type { Language } from '../contexts/TranslationContext';
import type { SharedAnswerResponse } from '../services/share';
import { truncateText } from './text';

const SHARE_PREVIEW_IMAGE_PATH = '/assets/logo/palen_social_card_20260710.png';
const SHARE_PREVIEW_IMAGE_WIDTH = '1200';
const SHARE_PREVIEW_IMAGE_HEIGHT = '627';
const SHARE_PREVIEW_IMAGE_ALT = 'Palen - IA dediee aux professionnels du transport ADR';
const SHARE_SEO_HEADING_MAX_LENGTH = 60;
const EMBEDDED_SHARED_ANSWER_ID = 'palen-shared-answer-data';

export function readEmbeddedSharedAnswer(): SharedAnswerResponse | null {
  const element = document.getElementById(EMBEDDED_SHARED_ANSWER_ID);
  if (element?.tagName !== 'SCRIPT' || element.getAttribute('type') !== 'application/json') {
    return null;
  }

  try {
    const answer: unknown = JSON.parse(element.textContent || '');
    return isSharedAnswerResponse(answer) ? answer : null;
  } catch {
    return null;
  }
}

export function applyNoIndexMetadata(title: string) {
  document.title = title;
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: 'noindex,nofollow,noarchive',
  });
}

export function applySharedAnswerMetadata(
  answer: SharedAnswerResponse,
  labels: {
    title: string;
  },
) {
  const url = window.location.href;
  const image = absoluteUrl(SHARE_PREVIEW_IMAGE_PATH);

  document.title = labels.title;
  upsertCanonical(url);
  upsertMeta('meta[name="description"]', { name: 'description', content: answer.seoDescription });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: labels.title });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: answer.seoDescription });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: image });
  upsertMeta('meta[property="og:image:width"]', { property: 'og:image:width', content: SHARE_PREVIEW_IMAGE_WIDTH });
  upsertMeta('meta[property="og:image:height"]', { property: 'og:image:height', content: SHARE_PREVIEW_IMAGE_HEIGHT });
  upsertMeta('meta[property="og:image:alt"]', { property: 'og:image:alt', content: SHARE_PREVIEW_IMAGE_ALT });
  upsertMeta('meta[property="og:locale"]', {
    property: 'og:locale',
    content: normalizeSharedAnswerLanguage(answer.locale) === 'en' ? 'en_US' : 'fr_FR',
  });
  const publicationDate = answer.questionCreatedAt || answer.createdAt;
  const revisionDate = answer.seoActivatedAt || answer.createdAt;
  upsertMeta('meta[property="article:published_time"]', {
    property: 'article:published_time',
    content: publicationDate,
  });
  upsertMeta('meta[property="article:modified_time"]', {
    property: 'article:modified_time',
    content: revisionDate,
  });
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: labels.title });
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: answer.seoDescription });
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: image });
  upsertMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt', content: SHARE_PREVIEW_IMAGE_ALT });
}

export function applyDocumentLanguage(language: Language) {
  document.documentElement.lang = language;
  document.documentElement.translate = true;
  document.querySelector('meta[name="google"][content="notranslate"]')?.remove();
}

export function sharedAnswerSeoTitle(answer: SharedAnswerResponse): string {
  return sharedAnswerHeading(answer);
}

export function normalizeSharedAnswerLanguage(locale: string | null): Language {
  return String(locale || 'fr').toLowerCase().startsWith('en') ? 'en' : 'fr';
}

export function sharedAnswerHeading(answer: SharedAnswerResponse): string {
  return truncateSeoText(
    answer.seoHeading || answer.question,
    SHARE_SEO_HEADING_MAX_LENGTH,
  );
}

function absoluteUrl(path: string) {
  return new URL(path, window.location.origin).toString();
}

function truncateSeoText(value: string, maxLength: number) {
  return truncateText(value, maxLength);
}

function isSharedAnswerResponse(value: unknown): value is SharedAnswerResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const answer = value as Record<string, unknown>;
  return typeof answer.question === 'string'
    && typeof answer.answer === 'string'
    && typeof answer.createdAt === 'string'
    && (answer.questionCreatedAt === null || typeof answer.questionCreatedAt === 'string')
    && (answer.locale === null || typeof answer.locale === 'string')
    && (answer.botProfileCode === null || typeof answer.botProfileCode === 'string')
    && typeof answer.seoEnabled === 'boolean'
    && typeof answer.protectedEnabled === 'boolean'
    && typeof answer.seoTitle === 'string'
    && typeof answer.seoDescription === 'string'
    && (answer.seoHeading === null || typeof answer.seoHeading === 'string')
    && (answer.seoActivatedAt === null || typeof answer.seoActivatedAt === 'string')
    && typeof answer.isRestrictedPreview === 'boolean';
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  const meta = document.querySelector<HTMLMetaElement>(selector) || document.createElement('meta');

  Object.entries(attributes).forEach(([key, value]) => {
    meta.setAttribute(key, value);
  });

  document.head.appendChild(meta);
}

function upsertCanonical(url: string) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]') || document.createElement('link');

  link.rel = 'canonical';
  link.href = url;
  document.head.appendChild(link);
}
